import { Controller } from '@hotwired/stimulus'

// I-UX-MAIL-01: live preview of an e-mail form (finance e-mail, supplier inquiry). Every edit re-plans the form on the
// server (POST to the form's own action with preview=1, debounced 300 ms) and the parts of the answer marked
// [data-email-live] that changed replace their counterparts by id: summary, errors, send button, fingerprint. The fields
// and the body editor (I-UX-MAILED-01) are never replaced, so typing is never interrupted. Nothing is computed here; the preview is the server's
// plan. Files are not sent with a preview (they become blobs only on submit).
export default class EmailComposeController extends Controller<HTMLFormElement> {
  private timer: number | undefined
  private request: AbortController | undefined
  private inFlight: Promise<void> | undefined
  private waitingToSend = false

  override disconnect(): void {
    window.clearTimeout(this.timer)
    this.request?.abort()
  }

  refresh(): void {
    window.clearTimeout(this.timer)
    this.timer = window.setTimeout(() => void this.load(), 300)
  }

  // I-UX-MAIL-03: Enter in a one-line text field previews at once and never sends (implicit submission would hit
  // Odeslat). Only the form's one-line inputs: checkboxes and file inputs keep their Enter (the body editor is a
  // frame, its keys never reach the form).
  enter(event: KeyboardEvent): void {
    const target = event.target
    if (!(target instanceof HTMLInputElement) || target.form !== this.element) return
    if (['checkbox', 'radio', 'file', 'submit', 'button'].includes(target.type)) return
    event.preventDefault()
    window.clearTimeout(this.timer)
    void this.load()
  }

  // I-UX-MAIL-04: a send right after an edit waits for that edit's preview (and its fingerprint), then sends if the
  // fresh plan still allows it. Further clicks meanwhile are dropped (one send). Runs before submit-once#guard, which
  // ignores the cancelled submit.
  submit(event: SubmitEvent): void {
    if (this.waitingToSend) {
      event.preventDefault()
      return
    }
    if (this.timer === undefined && this.inFlight === undefined) {
      this.confirmSend(event)
      return
    }
    event.preventDefault()
    this.waitingToSend = true
    window.clearTimeout(this.timer)
    void this.load().then(() => {
      this.waitingToSend = false
      const send = this.element.querySelector<HTMLButtonElement>('#email-send')
      if (send && !send.disabled) this.element.requestSubmit(send)
    })
  }

  // I-UXC-emails-4: Turbo skips this data-turbo=false form, so the send button's data-turbo-confirm (a 0 amount) is asked
  // here, once, right before the form really goes (after a held-back preview, on the re-submit).
  private confirmSend(event: SubmitEvent): void {
    const message = event.submitter?.getAttribute('data-turbo-confirm')
    if (message && !window.confirm(message)) event.preventDefault()
  }

  private load(): Promise<void> {
    this.timer = undefined
    this.request?.abort()
    const request = (this.request = new AbortController())
    const body = new FormData()
    for (const [name, value] of new FormData(this.element)) if (typeof value === 'string') body.append(name, value)
    body.append('preview', '1')
    const done = fetch(this.element.action, { method: 'POST', body, signal: request.signal, headers: { Accept: 'text/html' } })
      .then((response) => response.text())
      .then((html) => {
        if (!request.signal.aborted) this.swap(new DOMParser().parseFromString(html, 'text/html'))
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) throw error
      })
      .finally(() => {
        if (this.request === request) this.inFlight = undefined
      })
    this.inFlight = done
    return done
  }

  // Unchanged parts stay (no iframe reload, no repeated alert); a field's invalid state follows its error slot.
  private swap(page: Document): void {
    for (const fresh of page.querySelectorAll<HTMLElement>('[data-email-live]')) {
      const current = document.getElementById(fresh.id)
      if (current && !current.isEqualNode(fresh)) current.replaceWith(fresh)
    }
    for (const slot of this.element.querySelectorAll<HTMLElement>('[id^="email_errors_"]')) {
      const field = this.element.elements.namedItem(slot.id.replace('email_errors_', ''))
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue
      const invalid = (slot.textContent ?? '').trim() !== ''
      if (invalid) field.setAttribute('aria-invalid', 'true')
      else field.removeAttribute('aria-invalid')
      field.classList.toggle(field instanceof HTMLInputElement ? 'input-error' : 'textarea-error', invalid)
    }
  }
}
